import { UnknownAction, Dispatch } from 'redux';
import { v1 as uuid } from 'uuid';
import { updateArray } from '../../updateArray';
import { CandidateState, ElectionState, PartyState } from '../../_generatedTypes.g';
import { operationStatusSet, setOperationStatusWithDuration } from '../utils/operationSlice';
import { getElectionForPartyId } from '../../selectors';
import { ApiRequest, ReadAs, api } from '../../api';
import { SaveChangesResult } from '../../components/ui/InlineFormItem';
import { NavigateFunction } from 'react-router';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { voterEntityStateRefreshed, innerStateSet } from '../innerState/innerStateSlice';
import { candidateInlineEdited, candidateInlineValidatedErrors } from '../party/PartyScreenSlice';

export const removeInlineCandidateIfEmpty = (inlineEditingCandidateId: string | null, candidates: CandidateState[], dispatch: Dispatch<UnknownAction>) => {
    {
        if (inlineEditingCandidateId === null) {
            return;
        }

        const candidate = candidates.find(c => c.ClientSideId === inlineEditingCandidateId);
        if (candidate && candidate.Name.trim() === '') {
            dispatch(candidateRemoved(inlineEditingCandidateId));
        }
    }
}

export const deleteCandidate = async (candidate: CandidateState, token: string, parties: PartyState[], elections: ElectionState[], dispatch: Dispatch<UnknownAction>) => {
    dispatch(operationStatusSet({ warning: false, message: 'Deleting...', errors: [] }));
    const isCandidatesSystem = getElectionForPartyId(parties, elections, candidate.PartyId).CandidateSystem;
    const request: ApiRequest<void> = isCandidatesSystem
        ? api(token).Candidates.DeleteCandidateSystemCandidate(candidate.CandidateId, candidate.PartyId, {})
        : api(token).Candidates.Delete(candidate.CandidateId, {});
    return await request
        .ok(() => {
            setOperationStatusWithDuration({ warning: false, message: 'Deleted successfully', errors: [] }, 3, dispatch);
            dispatch(candidateRemoved(candidate.ClientSideId));
        })
        .anyStatus(errors => dispatch(operationStatusSet({ warning: true, message: 'An error occurred during the deletion.', errors })), ReadAs.ValidationErrors)
        .fail(() => setOperationStatusWithDuration({ warning: true, message: 'An error occurred during the deletion.', errors: [] }, 10, dispatch))
}
export const approveCandidacy = async (candidate: CandidateState, requestVerificationToken: string, dispatch: Dispatch<UnknownAction>) => {
    dispatch(operationStatusSet({ warning: true, message: 'Saving...', errors: [] }));

    const request: ApiRequest<void> = api(requestVerificationToken).Candidates.Approve(candidate.CandidateId, {});
    return await request
        .ok(() => {
            setOperationStatusWithDuration({ warning: false, message: 'Your changes were successfully saved.', errors: [] }, 3, dispatch);
            dispatch(candidateApproved(candidate.ClientSideId));
        })
        .anyStatus(errors => dispatch(operationStatusSet({ warning: true, message: 'An error occurred during saving.', errors })), ReadAs.ValidationErrors)
        .fail(() => setOperationStatusWithDuration({ warning: true, message: 'An error occurred during saving.', errors: [] }, 10, dispatch))
}

export const addBlankCandidateSystemCandidateAndSetEditMode = async (electionId: number, dispatch: Dispatch<UnknownAction>) => {
    const dummyPartyId = -1;
    const clientSideId = uuid();
    dispatch(blankCandidateSystemCandidateAdded({ electionId, partyId: dummyPartyId, clientSideId }));
            dispatch(candidateInlineEdited({candidateId: clientSideId}));
};

export const addNewCandidateSystemCandidateForCandidacy = async (electionId: number, userName: string, dispatch: Dispatch<UnknownAction>) => {
    const dummyPartyId = -1;
    const clientSideId = uuid();
    dispatch(newCandidateSystemCandidacyCandidateAdded({ electionId, partyId: dummyPartyId, clientSideId, userName: userName }));
};

export const saveCandidateSystemCandidate = async (candidate: CandidateState, name: string, token: string, dispatch: Dispatch<UnknownAction>): Promise<SaveChangesResult> => {
    return await api(token).Candidates.AddCandidateWithDummyParty<SaveChangesResult>(candidate.ElectionId, { ElectionId: candidate.ElectionId, Name: name, Position: 1 } as CandidateState)
        .ok(confirmedCandidate => {
            dispatch(candidateInlineEdited({candidateId: null}));
            const withClientSideId: CandidateState = { ...confirmedCandidate, ClientSideId: candidate.ClientSideId };
            dispatch(candidateReplaced(withClientSideId));
            return { success: true };
        }, ReadAs.Json)
        .anyStatus(errors => {
            dispatch(candidateInlineValidatedErrors({id: candidate.ClientSideId, errors}));
            return { errors: errors, success: false };
        }, ReadAs.ValidationErrors)
        .fail(() => ({ success: false, errors: [] }));
}

export const addCandidateSystemCandidate = async (candidate: CandidateState, token: string, dispatch: Dispatch<UnknownAction>, navigate: NavigateFunction): Promise<SaveChangesResult> => {
    return await api(token).Candidates.AddCandidateWithDummyParty<SaveChangesResult>(
        candidate.ElectionId,
        {
            ElectionId: candidate.ElectionId,
            Name: candidate.Name,
            Position: 1,
            Slogan_NL: candidate.Slogan_NL,
            Slogan_EN: candidate.Slogan_EN,
            ElectionText_NL: candidate.ElectionText_NL,
            ElectionText_EN: candidate.ElectionText_EN,
            Avatar: candidate.Avatar,
            ElectableInOther_NL: candidate.ElectableInOther_NL,
            ElectableInOther_EN: candidate.ElectableInOther_EN,
            UserName: candidate.UserName,
        } as CandidateState)
        .ok(confirmedCandidate => {
            const withClientSideId: CandidateState = { ...confirmedCandidate, ClientSideId: candidate.ClientSideId };
            dispatch(candidateReplaced(withClientSideId));
            navigate(`/election/${withClientSideId.ElectionId}`);
            return { success: true };
        }, ReadAs.Json)
        .anyStatus(errors => {
            dispatch(candidateInlineValidatedErrors({id: candidate.ClientSideId, errors}));
            return { errors: errors, success: false };
        }, ReadAs.ValidationErrors)
        .fail(() => ({ success: false, errors: [] }));
}


export const addBlankCandidateAndSetEditMode = async (partyId: number, parties: PartyState[], dispatch: Dispatch<UnknownAction>) => {
    const clientSideId = uuid();
    const party = parties.filter(x => x.PartyId === partyId)[0];
    dispatch(blankCandidateAdded({ electionId: party.ElectionId, partyId: party.PartyId, clientSideId }));
            dispatch(candidateInlineEdited({candidateId: clientSideId}));
};

export const saveBlankCandidate = async (candidate: CandidateState, name: string, userName: string, token: string, dispatch: Dispatch<UnknownAction>): Promise<SaveChangesResult> => {
    return await api(token).Parties.AddCandidateToParty<SaveChangesResult>(candidate.PartyId, { Name: name, UserName: userName, Position: 1 } as CandidateState)
        .ok(confirmedCandidate => {
            dispatch(candidateInlineEdited({candidateId: null}));
            const withClientSideId: CandidateState = { ...confirmedCandidate, ClientSideId: candidate.ClientSideId };
            dispatch(candidateReplaced(withClientSideId));
            return { success: true };
        }, ReadAs.Json)
        .anyStatus(errors => {
            dispatch(candidateInlineValidatedErrors({id: candidate.ClientSideId, errors}));
            return { errors, success: false };
        }, ReadAs.ValidationErrors)
        .fail(() => ({ success: false, errors: [] }));
}

export const saveCandidateName = async (candidate: CandidateState, newName: string, newUserName: string, token: string, dispatch: Dispatch<UnknownAction>): Promise<SaveChangesResult> => {

    const body: Partial<CandidateState> = {
        RowVersion: candidate.RowVersion,
        Name: newName,
        UserName: newUserName,
    };

    return await api(token).Candidates.EditName<SaveChangesResult>(candidate.PartyId, candidate.Position, body)
        .ok((_, response) => {
            const rowVersion = response.headers.get('ETag')!;
            const candidateWithNewNameAndRowVersion = { ...candidate, Name: newName, RowVersion: rowVersion };
            dispatch(candidateReplaced(candidateWithNewNameAndRowVersion));
            return { success: true };
        })
        .anyStatus(errors => ({ errors, success: false }), ReadAs.ValidationErrors)
}

export const saveCandidatePosition = async (movedCandidate: CandidateState, newPosition: number, token: string, dispatch: Dispatch<UnknownAction>): Promise<SaveChangesResult> => {

    const body: Partial<CandidateState> = {
        RowVersion: movedCandidate.RowVersion,
        Position: newPosition,
    };
    return await api(token).Candidates.EditPosition<SaveChangesResult>(movedCandidate.PartyId, movedCandidate.Position, body)
        .ok(fullFreshEntityState => {
            dispatch(voterEntityStateRefreshed(fullFreshEntityState));
            return { success: true };
        }, ReadAs.Json)
        .anyStatus(errors => {
            return { success: false, errors };
        }, ReadAs.ValidationErrors)
        .fail(() => ({ success: false, errors: [] }));
}

export const savePartyCandidatePosition = async (movedParty: PartyState, newPosition: number, token: string, dispatch: Dispatch<UnknownAction>): Promise<SaveChangesResult> => {
    const body: Partial<PartyState> = {
        RowVersion: movedParty.RowVersion,
        Position: newPosition,
    };
    return await api(token).Parties.EditPosition<SaveChangesResult>(movedParty.ElectionId, movedParty.Position, body)
        .ok(fullFreshEntityState => {
            dispatch(voterEntityStateRefreshed(fullFreshEntityState));
            return { success: true };
        }, ReadAs.Json)
        .anyStatus(errors => {
            return { success: false, errors };
        }, ReadAs.ValidationErrors)
        .fail(() => ({ success: false, errors: [] }));
}

const candidatesSlice = createSlice({
    name: 'SLICE_candidates',
    initialState: {} as CandidateState[],
    reducers: {
        candidateReplaced(state, action: PayloadAction<CandidateState>) {
            return updateArray(state, {
                with: _ => action.payload,
                where: c => c.ClientSideId === action.payload.ClientSideId,
            });
        },
        candidateApproved(state, action: PayloadAction<string>) {
            return updateArray(state, {
                with: c => ({
                    ...c,
                    IsApproved: true,
                }),
                where: c => c.ClientSideId === action.payload,
            });
        },
        blankCandidateAdded(state, action: PayloadAction<{ electionId: number, partyId: number, clientSideId: string }>) {
            return [
                ...state,
                { ClientSideId: action.payload.clientSideId, Name: '', CandidateId: 0, ElectionId: action.payload.electionId, PartyId: action.payload.partyId } as CandidateState,
            ];
        },
        candidateRemoved(state, action: PayloadAction<string>) {
            return state.filter(x => x.ClientSideId !== action.payload);
        },
        blankCandidateSystemCandidateAdded(state, action: PayloadAction<{ electionId: number, partyId: number, clientSideId: string }>) {
            return [
                ...state,
                { ClientSideId: action.payload.clientSideId, Name: '', CandidateId: 0, ElectionId: action.payload.electionId, PartyId: action.payload.partyId } as CandidateState,
            ];
        },
        newCandidateSystemCandidacyCandidateAdded(state, action: PayloadAction<{ electionId: number, partyId: number, clientSideId: string, userName: string }>) {
            return [
                ...state,
                { ClientSideId: action.payload.clientSideId, Name: '', CandidateId: 0, ElectionId: action.payload.electionId, PartyId: action.payload.partyId, UserName: action.payload.userName, Position: 9999 } as CandidateState,
            ];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(innerStateSet, (state, action) => {
        if (action.payload.Type === 'LOGGED_IN_STATE') {
            return assignClientSideIds(action.payload.VoterEntities.Candidates);
        } else {
            return [];
        }
    });
    builder.addCase(voterEntityStateRefreshed, (state, action) => assignClientSideIds(action.payload.Candidates));
  },
})

export const {
    candidateReplaced,
    candidateApproved,
    blankCandidateAdded,
    candidateRemoved,
    blankCandidateSystemCandidateAdded,
    newCandidateSystemCandidacyCandidateAdded,
} = candidatesSlice.actions

export default candidatesSlice.reducer
function assignClientSideIds(entities: CandidateState[]) {
    return entities.map(candidate => ({
        ...candidate,
        ClientSideId: uuid(),
    }));
}
